/**
 * Settings — sub-page of the Me tab (T25).
 *
 * Layout:
 *   Back chevron (top-left, ≥44px tap) → navigate(-1)
 *   Title "Settings"
 *
 * Sections:
 *   1. Notifications → /settings/notifications
 *   2. Privacy       → stub modal "Available shortly"
 *   3. Language      → displays current languageCode; Change btn disabled
 *   4. Delete account → confirm modal; Delete shows "Available shortly" inline
 *   5. About         → static version + domain text
 *
 * Mounted OUTSIDE TabShell — it is a sub-page, not a tab.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../components/chrome/MeSubpageShell';
import { useMe } from '../hooks/useMe';
import styles from './Settings.module.css';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface RowProps {
  label: string;
  value?: string;
  onClick: () => void;
  'data-testid'?: string;
}

function SettingsRow({ label, value, onClick, 'data-testid': testId }: RowProps): JSX.Element {
  return (
    <button
      type="button"
      className={styles.row}
      onClick={onClick}
      data-testid={testId}
    >
      <span className={styles.rowLabel}>{label}</span>
      {value !== undefined && (
        <span className={styles.rowValue}>{value}</span>
      )}
      <span className={styles.rowChevron} aria-hidden="true">›</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Delete account confirm modal
// ---------------------------------------------------------------------------

interface DeleteModalProps {
  onCancel: () => void;
  onDelete: () => void;
  stubMessage: string | null;
}

function DeleteModal({ onCancel, onDelete, stubMessage }: DeleteModalProps): JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('hub.aria.deleteDialog')}>
      <div className={styles.modalSheet}>
        <p className={styles.modalTitle}>{t('hub.delete.title')}</p>
        <p className={styles.modalBody}>
          {t('hub.delete.body')}
        </p>
        {stubMessage && (
          <p
            className={styles.modalCaption}
            role="alert"
            data-testid="delete-stub-message"
          >
            {stubMessage}
          </p>
        )}
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalCancelBtn}
            onClick={onCancel}
            data-testid="delete-cancel-btn"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            className={styles.modalDeleteBtn}
            onClick={onDelete}
            data-testid="delete-confirm-btn"
          >
            {t('common:actions.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings screen
// ---------------------------------------------------------------------------

export function Settings(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['settings', 'common']);
  const { data: me } = useMe();

  const [showDelete, setShowDelete] = useState(false);
  const [deleteStubMsg, setDeleteStubMsg] = useState<string | null>(null);

  const handleDelete = (): void => {
    // Stub: no backend endpoint yet
    setDeleteStubMsg(t('hub.delete.stub'));
  };

  return (
    <MeSubpageShell title={t('title')} backTestId="settings-back-btn">
      <p className={styles.intro} data-testid="settings-intro">{t('hub.intro')}</p>

      {/* ── Notifications ────────────────────────────────────────── */}
      <section className={styles.section} aria-label={t('hub.aria.notifications')}>
        <p className={styles.sectionLabel}>{t('sections.notifications')}</p>
        <SettingsRow
          label={t('hub.notificationPrefs')}
          onClick={() => navigate('/settings/notifications')}
          data-testid="settings-notifications-row"
        />
      </section>

      {/* ── Privacy ──────────────────────────────────────────────── */}
      <section className={styles.section} aria-label={t('hub.aria.privacy')}>
        <p className={styles.sectionLabel}>{t('sections.privacy')}</p>
        <SettingsRow
          label={t('hub.privacyControls')}
          onClick={() => navigate('/settings/privacy')}
          data-testid="settings-privacy-row"
        />
      </section>

      {/* ── Language ─────────────────────────────────────────────── */}
      <section className={styles.section} aria-label={t('hub.aria.language')}>
        <p className={styles.sectionLabel}>{t('sections.preferences')}</p>
        <div className={styles.languageRow} data-testid="settings-language-row">
          <span className={styles.rowLabel}>
            {t('hub.language')}
          </span>
          <span className={styles.languageCode} data-testid="settings-language-code">
            {me?.languageCode ?? '—'}
          </span>
          <button
            type="button"
            className={styles.changeBtn}
            onClick={() => navigate('/settings/language')}
            data-testid="settings-language-change-btn"
          >
            {t('hub.change')}
          </button>
        </div>

      </section>

      {/* ── Account ──────────────────────────────────────────────── */}
      <section className={styles.section} aria-label={t('hub.aria.account')}>
        <p className={styles.sectionLabel}>{t('sections.account')}</p>
        <SettingsRow
          label={t('hub.profileBirthData')}
          onClick={() => navigate('/settings/account')}
          data-testid="settings-account-row"
        />
        <SettingsRow
          label={t('account.delete.label')}
          onClick={() => { setDeleteStubMsg(null); setShowDelete(true); }}
          data-testid="settings-delete-row"
        />
      </section>

      {/* ── About ────────────────────────────────────────────────── */}
      <section className={styles.section} aria-label={t('hub.aria.about')}>
        <p className={styles.sectionLabel}>{t('sections.about')}</p>
        <div className={styles.aboutBlock} data-testid="settings-about-block">
          <p className={styles.aboutText}>{t('footer.version')}</p>
          <p className={styles.aboutLink}>{t('footer.url')}</p>
        </div>
      </section>

      {showDelete && (
        <DeleteModal onCancel={() => setShowDelete(false)} onDelete={handleDelete} stubMessage={deleteStubMsg} />
      )}
    </MeSubpageShell>
  );
}
